// Core imports
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

// Component imports
import { Card, Col, Empty, Row, Skeleton, Typography } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import { FacebookIcon } from '../components/Icons/FacebookIcon';
import { XIcon } from '../components/Icons/XIcon';
import { LinkedinIcon } from '../components/Icons/LinkedinIcon';
import { WebsiteIcon } from '../components/Icons/WebsiteIcon';
import SEO from '../components/SEO/SEO';

// Misc imports
import api from '../api';
import { NAV_BLOG } from '../constants';

const { Title, Paragraph } = Typography;

const BlogAuthorScreen = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [author, setAuthor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.fetchBlogAuthor({ slug })
      .then(setAuthor)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="landing-container blog-author-page">
        <Skeleton active avatar paragraph={{ rows: 4 }} />
      </div>
    );
  }

  if (notFound || !author) {
    return (
      <div className="landing-container" style={{ paddingTop: 80, paddingBottom: 80, textAlign: 'center' }}>
        <Title level={2}>Author not found</Title>
        <Paragraph><Link to={NAV_BLOG}>← Back to blog</Link></Paragraph>
      </div>
    );
  }

  const initials = author.name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?';
  const siteUrl = process.env.REACT_APP_SITE_URL || 'https://ndearchive.com';

  return (
    <>
      <SEO
        title={`${author.name} | Author`}
        description={author.bio ? author.bio.slice(0, 160) : `Articles by ${author.name} on NDE Archive Blog.`}
        path={`/blog/author/${author.slug}`}
        type="profile"
        image={author.pictureUrl || undefined}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'ProfilePage',
          mainEntity: {
            '@type': 'Person',
            name: author.name,
            url: `${siteUrl}/blog/author/${author.slug}`,
            image: author.pictureUrl || undefined,
            description: author.bio || undefined,
            sameAs: [
              author.linkedinUrl,
              author.websiteUrl
            ].filter(Boolean)
          }
        }}
      />

      <div className="landing-container blog-author-page">
        {/* Author header */}
        <div className="blog-author-header">
          {author.pictureUrl ? (
            <img src={author.pictureUrl} alt={author.name} className="blog-author-avatar-lg" />
          ) : (
            <div className="blog-author-avatar-placeholder-lg">{initials}</div>
          )}

          <div className="blog-author-info">
            <Title level={1} style={{ marginBottom: 8 }}>{author.name}</Title>

            {author.bio && (
              <Paragraph className="blog-author-bio text-color-black-80">
                {author.bio}
              </Paragraph>
            )}

            <div className="blog-author-links">
              {author.linkedinUrl && (
                <a href={author.linkedinUrl} target="_blank" rel="noopener noreferrer" title="LinkedIn">
                  <LinkedinIcon style={{ fontSize: 22 }} />
                </a>
              )}
              {author.facebookUrl && (
                <a href={author.facebookUrl} target="_blank" rel="noopener noreferrer" title="Facebook">
                  <FacebookIcon style={{ fontSize: 22 }} />
                </a>
              )}
              {author.xUrl && (
                <a href={author.xUrl} target="_blank" rel="noopener noreferrer" title="X">
                  <XIcon style={{ fontSize: 22 }} />
                </a>
              )}
              {author.websiteUrl && (
                <a href={author.websiteUrl} target="_blank" rel="noopener noreferrer" title="Website">
                  <WebsiteIcon style={{ fontSize: 22 }} />
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Posts */}
        <Title level={3} style={{ marginBottom: 24 }}>
          Articles by {author.name}
        </Title>

        {author.posts?.length === 0 ? (
          <Empty description="No published articles yet." />
        ) : (
          <Row gutter={[24, 24]}>
            {author.posts?.map(post => {
              const publishedDate = post.publishedAt
                ? new Date(post.publishedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                : null;

              return (
                <Col key={post.id} xs={24} sm={12} lg={8}>
                  <Card
                    className="blog-card"
                    bordered={false}
                    cover={
                      post.coverImageUrl ? (
                        <img src={post.coverImageUrl} alt={post.title} className="blog-card-cover" />
                      ) : (
                        <div className="blog-card-cover-placeholder">✍️</div>
                      )
                    }
                  >
                    {post.tags?.length > 0 && (
                      <div className="blog-card-tags">
                        {post.tags.map(tag => (
                          <span
                            key={tag.id}
                            className="blog-tag-badge"
                            onClick={() => navigate(`${NAV_BLOG}?tag=${tag.slug}`)}
                          >
                            {tag.name}
                          </span>
                        ))}
                      </div>
                    )}

                    <div
                      className="blog-card-title cursor-pointer"
                      onClick={() => navigate(`/blog/${post.slug}`)}
                    >
                      {post.title}
                    </div>

                    {post.excerpt && (
                      <Paragraph className="blog-card-excerpt">{post.excerpt}</Paragraph>
                    )}

                    {publishedDate && (
                      <div className="blog-card-footer" style={{ borderTop: '1px solid var(--b-20)', paddingTop: 12, marginTop: 12 }}>
                        <CalendarOutlined />
                        <span className="blog-card-date">{publishedDate}</span>
                      </div>
                    )}
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </div>
    </>
  );
};

export default BlogAuthorScreen;
